// src/app/components/foundation/WorkspaceSection.tsx
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';

export interface WorkspaceSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}

/**
 * Shared collapsible section shell for a `Workspace` panel. Every domain
 * section inside a workspace (summary, timeline, payment, notes, ...)
 * renders through this so panels stay visually consistent regardless of
 * feature. Moved here unchanged from the Orders feature's order-detail
 * workspace, which was the first consumer.
 */
export function WorkspaceSection({ title, defaultOpen = false, children }: WorkspaceSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-2 py-2.5 text-right"
      >
        <span className="text-xs font-semibold text-[var(--vania-colors-text-primary,#f5f6fa)]">{title}</span>
        <ChevronDown
          size={14}
          className={clsx(
            'shrink-0 text-[var(--vania-colors-text-secondary,#a3aab8)] transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  );
}
